import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, collection, addDoc, query, orderBy, limit, getDocs, doc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

function ensureApp() {
  try {
    if (typeof getApps === 'function' && getApps().length === 0) {
      initializeApp(firebaseConfig);
    } else {
      // ensure getApp doesn't throw
      try { getApp(); } catch (e) { initializeApp(firebaseConfig); }
    }
  } catch (e) {
    // Initialization failure should not crash the app during dev
    console.warn('[firebase] init failed', e);
  }
}

function getDB() {
  ensureApp();
  return getFirestore();
}

/**
 * Sign in anonymously (once per session) so Firestore writes pass the
 * `request.auth != null` security rule. Requires the Anonymous provider
 * to be enabled in the Firebase console (Authentication → Sign-in method).
 */
async function ensureAuth() {
  ensureApp();
  const auth = getAuth();
  if (auth.currentUser) return auth.currentUser;
  const cred = await signInAnonymously(auth);
  return cred.user;
}

/**
 * Save a planet record to Firestore collection `planets`.
 * record should be a plain object. Chords should be serialized (string) by caller.
 * `savedAt` is stamped server-side so recency ordering can't be spoofed.
 * Returns the new document id.
 */
export async function savePlanet(record) {
  try {
    const user = await ensureAuth();
    const db = getDB();
    const col = collection(db, 'planets');
    const docRef = await addDoc(col, {
      ...record,
      ownerUid: user.uid,      // so the chain never hands you your own song
      deliveredCount: 0,       // times this song has been handed to a traveler
      savedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (e) {
    console.warn('[firebase] savePlanet failed', e);
    throw e;
  }
}

/**
 * 1:1 delivery chain: fetch the LEAST-delivered (oldest first) song that
 * isn't your own, then mark it delivered. Every traveler's song reliably
 * reaches someone instead of everyone racing for the newest upload.
 * Direct ?planet= links bypass this (deliberate sharing) via loadPlanetById.
 * Falls back to the most recent planet on any failure.
 */
export async function loadNextPlanetInChain() {
  try {
    const user = await ensureAuth().catch(() => null);
    const db = getDB();
    const col = collection(db, 'planets');
    const q = query(col, orderBy('deliveredCount', 'asc'), orderBy('savedAt', 'asc'), limit(5));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return loadMostRecentPlanet();

    const docs = snapshot.docs;
    const pick = docs.find(d => !user || d.data().ownerUid !== user.uid) || docs[0];

    // Mark delivered (best-effort — rules only allow +1 on deliveredCount)
    try {
      await updateDoc(doc(db, 'planets', pick.id), { deliveredCount: increment(1) });
    } catch (e) {
      console.warn('[firebase] could not mark delivery', e);
    }

    return { id: pick.id, ...pick.data() };
  } catch (e) {
    console.warn('[firebase] loadNextPlanetInChain failed, falling back', e);
    return loadMostRecentPlanet();
  }
}

/**
 * Fetch the most recently saved planet document from Firestore.
 * Returns a plain object { id, name, position, songName, chords, songData, ... } or null.
 */
export async function loadMostRecentPlanet() {
  try {
    const db = getDB();
    const col = collection(db, 'planets');
    const q = query(col, orderBy('savedAt', 'desc'), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (e) {
    console.warn('[firebase] loadMostRecentPlanet failed', e);
    return null;
  }
}

/**
 * Fetch a pool of recent player planets to scatter across the galaxy as
 * "singing stars" — real people's songs the player can fly to.
 */
export async function listSingingPlanets(max = 24) {
  try {
    const db = getDB();
    const col = collection(db, 'planets');
    const q = query(col, orderBy('savedAt', 'desc'), limit(max));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[firebase] listSingingPlanets failed', e);
    return [];
  }
}

/**
 * Fetch a specific planet document by ID.
 */
export async function loadPlanetById(id) {
  try {
    const db = getDB();
    const docRef = doc(db, 'planets', id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() };
  } catch (e) {
    console.warn('[firebase] loadPlanetById failed', e);
    return null;
  }
}
